import React from 'react';
import {Redirect} from 'react-router-dom';
import {Box, Grid, Typography} from '@mui/material';
import IndividualDashboardShell from '../components/IndividualDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {NAVY, PageHeader, StatCard} from '../components/widgets.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, kpiText} from '../../DashboardCommon/dashboardCards';
import CardTable from '../../DashboardCommon/CardTable.comp';
import {LEARNING_PROGRESS_SECTION_CODE, learningProgramPresence} from '../learningProgramPresence';

/* Slide 9 — Learning Impact Overview. Live data (Data Map ID-09-01 … 04):
     KPIs      ID.S_5.C_2 Active Programs · C_3 Passed Programs ("n of N") · C_4 Avg Impact Rate
     My programs ID.S_5.C_5 (program · target COS area · pre → post · pass / not pass · impact rate · signal)
   The table is drawn with the columns the card sends. The page used to show
   fixed sample figures (5 / 3 of 10 / 37%) and three sample programs. */
const CARD_SECTIONS = [LEARNING_PROGRESS_SECTION_CODE];

const LearningProgressPage = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });

    if (loading) {
        return (
            <IndividualDashboardShell>
                <CircularLoader />
            </IndividualDashboardShell>
        );
    }

    // No program linked yet: the page is hidden from the menu, so send a direct visit to Overview.
    // Missing data / S_5 ('unknown') keeps the old behaviour so a failed fetch does not bounce the learner.
    if (learningProgramPresence(dashboardData) === 'none') {
        return <Redirect to="/dq_pro/individual-dashboard/score-overview"/>;
    }

    if (!dashboardData) {
        return (
            <IndividualDashboardShell>
                <NoDataAvailable />
            </IndividualDashboardShell>
        );
    }

    const card = (c) => findCard(dashboardData, LEARNING_PROGRESS_SECTION_CODE, c);
    const passed = card('C_3');

    return (
        <IndividualDashboardShell>
            <PageHeader
                title="Learning Impact Overview"
                subtitle="Learning KPIs with per-program target areas, coverage and impact signal."
            />

            <Grid container spacing={2.5} sx={{mb: 3}}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard label="Active Programs" value={kpiText(card('C_2'))} sub="programs"/>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {/* The card already reads "n of N". */}
                    <StatCard label="Passed Programs" value={kpiText(passed)} sub="programs"/>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard label="Avg Impact Rate" value={kpiText(card('C_4'), true)}/>
                </Grid>
            </Grid>

            <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 1.5}}>
                {card('C_5')?.title || 'My programs'}
            </Typography>
            <Box>
                <CardTable card={card('C_5')} emptyText="No learning programs assigned to you yet." minWidth={760}/>
            </Box>
        </IndividualDashboardShell>
    );
};

export default LearningProgressPage;
